import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { ThemeProps } from '@styles/theme';
import { ScatterEvent, TypeMeta, resolveTypeMeta } from '../EventScatterShape';
import { useStyles } from './EventTimeline.styles';

function timeFormatterDuration(value: number | undefined): string {
    const s = Math.max(0, Math.floor(value!));
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    const pad2 = (n: number) => String(n).padStart(2, '0');

    return hours > 0
        ? `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
        : `${pad2(minutes)}:${pad2(seconds)}`;
}

// ── helpers (mirrored from EventScatterTooltip) ──────────────────────────────

function tryFormatDateTime(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {return value;}
    const parsedMs = Date.parse(trimmed);
    if (!Number.isFinite(parsedMs)) {return value;}
    if (!/[t:\-/]/i.test(trimmed)) {return value;}

    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(new Date(parsedMs));
}

function formatDisplayValue(rawValue: unknown): string {
    if (rawValue == null) {return '—';}
    if (typeof rawValue === 'string') {return tryFormatDateTime(rawValue);}
    if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {return String(rawValue);}
    try {
        return JSON.stringify(rawValue);
    } catch {
        return '—';
    }
}

function toEntries(value: unknown): Array<{ key: string; value: string }> {
    if (value == null) {return [];}
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return [{ key: 'Value', value: formatDisplayValue(value) }];
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
        return Object.entries(value as Record<string, unknown>)
            .filter(([key]) => !key.startsWith('_'))
            .map(([key, rawValue]) => ({ key, value: formatDisplayValue(rawValue) }));
    }
    try {
        return [{ key: 'Value', value: formatDisplayValue(value) }];
    } catch {
        return [];
    }
}

function nicifyKeyLabel(value: string): string {
    return value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();
}

function typeLabel(type: string, meta: TypeMeta): string {
    return meta.label ?? type.split('_')[0];
}

// ── EventTimelineRow ─────────────────────────────────────────────────────────

type EventTimelineRowProps = {
    event: ScatterEvent;
    typeMeta?: Record<string, TypeMeta>;
    styles: ReturnType<typeof useStyles>;
    isLast: boolean;
    isDark?: boolean;
};

function EventTimelineRow({ event, typeMeta, styles, isLast, isDark = false }: EventTimelineRowProps) {
    const [expanded, setExpanded] = useState(false);

    const meta = resolveTypeMeta(event.type, typeMeta, undefined, isDark);
    const label = (event.label ?? meta.label ?? event.type).replace(/([A-Z])/g, ' $1').trim();
    const badgeLabel = typeLabel(event.type, meta).toUpperCase();
    const timeStr = timeFormatterDuration(event.time);
    const description = event.description ?? '';
    const valueEntries = toEntries(event.value);
    const hasDetails = !!description || valueEntries.length > 0;

    const dotStyle = { borderColor: meta.color, backgroundColor: expanded ? meta.color : `${meta.color}28` };
    const cardStyle = expanded
        ? { borderColor: `${meta.color}55` }
        : {};

    return (
        <div className={styles.eventRow}>
            <div className={styles.track}>
                <div className={styles.dot} style={dotStyle} />
                <div className={styles.timeTag} style={{ color: meta.color }}>
                    {timeStr}
                </div>
                {!isLast && <div className={styles.connector} />}
            </div>

            <div
                className={`${styles.card}${expanded ? ` ${styles.cardExpanded}` : ''}`}
                style={cardStyle}
                onClick={() => hasDetails && setExpanded(prev => !prev)}
            >
                {/* ── Header ── */}
                <div className={styles.cardHeader}>
                    <div className={styles.cardLeft}>
                        {description && (
                            <div
                                className={`${styles.descriptionMain}${!expanded ? ` ${styles.descriptionMainTruncated}` : ''}`}
                                title={!expanded ? description : undefined}
                            >
                                {description}
                            </div>
                        )}
                        <div className={styles.labelRow}>
                            <span
                                className={styles.typeBadge}
                                style={{
                                    backgroundColor: isDark ? `${meta.color}55` : `${meta.color}1a`,
                                    color: meta.color,
                                    border: `1px solid ${meta.color}${isDark ? 'aa' : '55'}`,
                                }}
                            >
                                {badgeLabel}
                            </span>
                            <span className={styles.eventLabel} title={label}>
                                {label}
                            </span>
                        </div>
                    </div>

                    {hasDetails && (
                        <div className={styles.cardRight}>
                            <span
                                className={`${styles.chevron}${expanded ? ` ${styles.chevronOpen}` : ''}`}
                                aria-hidden="true"
                            >
                                ▾
                            </span>
                        </div>
                    )}
                </div>

                {/* ── Expanded body ── */}
                {expanded && (
                    <div className={styles.expandedBody}>
                        {valueEntries.length > 0 && (
                            <>
                                <div className={styles.expandedBodyTitle}>Details</div>
                                <div className={styles.kvGrid}>
                                    {valueEntries.map(entry => (
                                        <React.Fragment key={entry.key}>
                                            <div className={styles.kvKey} title={entry.key}>
                                                {nicifyKeyLabel(entry.key)}
                                            </div>
                                            <div className={styles.kvValue}>{entry.value}</div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── EventTimeline ─────────────────────────────────────────────────────────────

export type EventTimelineProps = {
    events?: ScatterEvent[];
    typeMeta?: Record<string, TypeMeta>;
};

export const EventTimeline: React.FC<EventTimelineProps> = ({ events, typeMeta }) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const sorted = (events ?? []).slice().sort((a, b) => a.time - b.time);

    if (!sorted.length) {
        return <div className={styles.empty}>No Events Detected</div>;
    }

    return (
        <div className={styles.root}>
            {sorted.map((event, index) => (
                <EventTimelineRow
                    key={`${event.type}-${event.time}-${event.label}-${index}`}
                    event={event}
                    typeMeta={typeMeta}
                    styles={styles}
                    isLast={index === sorted.length - 1}
                    isDark={theme.isDark}
                />
            ))}
        </div>
    );
};
